import { CreditInfo, UserMetadataType } from '../services/types';

export const flattenMetaDataOrdersAndTracking = (
  metaDataWithOrdersAndTracking?: Pick<UserMetadataType, 'orders' | 'trackingInfo'>,
) => {
  if (!metaDataWithOrdersAndTracking) {
    return {};
  }

  const { orders, trackingInfo } = metaDataWithOrdersAndTracking;

  const flattenedOrders = (orders ?? [])
    .flatMap((order) =>
      (order.shipments ?? []).flatMap((shipment) => {
        if (!shipment) return [];

        return {
          orderId: order.orderId,
          billingDate: order.billingDate,
          orderShipmentId: shipment.shipmentDetails?.orderShipmentId ?? '',
          trackingNumber: shipment.shipmentDetails?.trackingNumber ?? '',
          status: shipment.shipmentDetails?.status ?? '',
          carrier: shipment.shipmentDetails?.carrier ?? '',
        };
      }),
    )
    .reduce(
      (acc, shipment, index) => {
        const i = index + 1;
        acc[`orderId${i}`] = shipment.orderId;
        acc[`billingDate${i}`] = shipment.billingDate;
        acc[`orderShipment${i}`] = shipment.orderShipmentId;
        acc[`trackingNumber${i}`] = shipment.trackingNumber;
        acc[`status${i}`] = shipment.status;
        acc[`carrier${i}`] = shipment.carrier;
        return acc;
      },
      {} as Record<string, string | number>,
    );

  const flattenedTracking = Object.values(trackingInfo?.trackings ?? [])
    .flatMap((tracking) => {
      if (!tracking) return [];

      const lastCheckpoint = tracking.checkpoints?.[tracking.checkpoints.length - 1] ?? {};

      return {
        orderId: tracking.orderId,
        estimatedDeliveryDate: tracking.estimatedDeliveryDate ?? '',
        carrier: tracking.shipmentDetails?.carrier ?? '',
        trackingNumber: tracking.shipmentDetails?.trackingNumber ?? '',
        shipmentDate: tracking.shipmentDetails?.shipmentDate ?? '',
        status: tracking.shipmentDetails?.status ?? '',
        warehouse: tracking.shipmentDetails?.warehouse ?? '',
        carrierTrackingUrl: tracking.shipmentDetails?.carrierTrackingUrl ?? '',
        lastCheckpointTime: lastCheckpoint.checkpointTime ?? '',
        lastCheckpointCountry: lastCheckpoint.countryName ?? '',
        lastCheckpointLocation: lastCheckpoint.location ?? '',
        lastCheckpointMessage: lastCheckpoint.message ?? '',
        lastCheckpointTag: lastCheckpoint.tag ?? '',
        lastCheckpointZipcode: lastCheckpoint.zipcode ?? '',
      };
    })
    .reduce(
      (acc, tracking, index) => {
        const i = index + 1;
        acc[`trackingOrderId${i}`] = tracking.orderId;
        acc[`estimatedDeliveryDate${i}`] = tracking.estimatedDeliveryDate;
        acc[`trackingCarrier${i}`] = tracking.carrier;
        acc[`trackingNumber${i}`] = tracking.trackingNumber;
        acc[`shipmentDate${i}`] = tracking.shipmentDate;
        acc[`trackingStatus${i}`] = tracking.status;
        acc[`warehouse${i}`] = tracking.warehouse;
        acc[`carrierTrackingUrl${i}`] = tracking.carrierTrackingUrl;
        acc[`lastCheckpointTime${i}`] = tracking.lastCheckpointTime;
        acc[`lastCheckpointCountry${i}`] = tracking.lastCheckpointCountry;
        acc[`lastCheckpointLocation${i}`] = tracking.lastCheckpointLocation;
        acc[`lastCheckpointMessage${i}`] = tracking.lastCheckpointMessage;
        acc[`lastCheckpointTag${i}`] = tracking.lastCheckpointTag;
        acc[`lastCheckpointZipcode${i}`] = tracking.lastCheckpointZipcode;
        return acc;
      },
      {} as Record<string, string | number>,
    );

  return {
    ...flattenedOrders,
    ...flattenedTracking,
  };
};

export const formatCredits = (credits: CreditInfo[]): string => {
  return credits
    .map(
      (credit, index) => `Credit #${index + 1}: '${credit.creditName}', amount: ${credit.amount}`,
    )
    .join('; ');
};
